import * as amplitude from "@amplitude/analytics-browser";
import { Alert, Button } from "antd";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Spinner from "../components/Loading/Spinner";
import Store from "../stateStore";
import { getBackendUrl } from "../utils";
import ConfirmationScreen from "./ConfirmationScreen";
import CreditCardInputForm from "./CreditCardInputForm";
import ErrorScreen from "./ErrorScreen";
import { parseQueryParams, postMessageToOpener } from "./utils";

export default function MerchantAuth() {
  const history = useHistory();

  const { id: sessionId } = useParams<{ id: string }>();
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [showCardInputForm, setShowCardInputForm] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [latestSessionId, setLatestSessionId] = useState<string | null>(null);
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState<string | null>(null);
  const [vendorLogo, setVendorLogo] = useState<string | null>(null);
  const [cardActivationId, setCardActivationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isManualCompleting, setIsManualCompleting] = useState(false);
  const [manualCompleteError, setManualCompleteError] = useState<string | null>(
    null,
  );
  const [cardSubmissionError, setCardSubmissionError] = useState<string | null>(
    null,
  );

  const stateStore = Store.useStore();
  const orgId = stateStore.get("orgId");
  const { origin, nonce } = parseQueryParams(history.location.search);

  const setSessionDetails = (sessionData: any) => {
    setLatestSessionId(sessionData.sessionId ?? sessionId);
    setSessionUrl(sessionData.liveViewUrl);
    setVendorName(sessionData.vendorName);
    setVendorLogo(sessionData.vendorLogo);
    setOrganizationName(sessionData.orgName);
    setCardActivationId(sessionData.cardActivationId);
  };

  const withNoNavbar = (url: string) => {
    if (!url) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}navbar=false`;
  };

  const handleApiError = (err: unknown): boolean => {
    if (!axios.isAxiosError(err)) {
      return false;
    }

    console.log("[v0] Checking API error:", err.response?.data);
    const errorMessage = err.response?.data?.error_message;

    if (!errorMessage) {
      return false;
    }

    // Display whatever error message the backend sends
    setError(errorMessage);
    return true;
  };

  useEffect(() => {
    loadSessionFromId(sessionId);

    return () => {
      if (pollingRef.current) {
        stopPolling();
      }
    };
  }, [sessionId]);

  useEffect(() => {
    if (showConfirmation || showCardInputForm) {
      // Don't start polling until confirmation step is completed.
      return;
    }

    if (sessionId) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [sessionId, showConfirmation]);

  const startPolling = () => {
    if (pollingRef.current) {
      stopPolling();
    }

    const checkMerchantAuth = async () => {
      try {
        console.log(`[v0] Polling merchant auth for session: ${sessionId}`);
        const response = await axios.get(
          `${getBackendUrl()}/supplier_enablement/check_merchant_auth/${sessionId}`,
        );
        console.log("[v0] Merchant auth check result:", response.data);

        // Update URL in case a new popup window needs to be displayed. Only needed for Browserbase since
        // Hyperbrowser handles this automatically.
        if (response.data.browser === "BROWSERBASE") {
          setSessionUrl(response.data.liveViewUrl);
        }

        // Check if user is logged in
        if (response.data && response.data.isLoggedIn === true) {
          console.log(
            "[v0] User logged in detected, redirecting to complete page",
          );
          stopPolling();
          history.push("/complete");
          return;
        }
      } catch (err) {
        console.log("[v0] Error checking merchant auth:", err);
        if (handleApiError(err)) {
          stopPolling();
          return;
        }
      }
    };

    checkMerchantAuth();
    pollingRef.current = setInterval(checkMerchantAuth, 5000);
  };

  // Must be a callback so stopPolling picks up on updated versions of pollingInterval.
  const stopPolling = () => {
    if (pollingRef.current) {
      console.log("[v0] Stopping polling merchant auth");
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      console.log("[v0] Stopped polling merchant auth");
    }
  };

  const handleManualComplete = async () => {
    if (!sessionId) {
      console.error("[v0] Cannot complete login: sessionId is null");
      setManualCompleteError("Session ID not found");
      return;
    }

    setIsManualCompleting(true);
    setManualCompleteError(null);

    try {
      console.log("[v0] Manually completing login for session:", sessionId);
      await axios.post(
        `${getBackendUrl()}/supplier_enablement/merchant_auth/${sessionId}/login_complete`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("[v0] Manual login completion successful");
      amplitude.track("Merchant Auth Manual Login Complete", {
        orgId,
        sessionId,
        cardActivationId,
      });

      // Post message to opener if in popup
      try {
        postMessageToOpener(sessionId, nonce, origin);
      } catch (e) {
        history.push("/error");
        return;
      }

      // Redirect to complete page
      history.push("/complete");
    } catch (err) {
      console.error("[v0] Error completing login manually:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to complete login";
      setManualCompleteError(errorMessage);
    } finally {
      setIsManualCompleting(false);
    }
  };

  const loadSessionFromId = async (
    id: string,
    options?: { setLoading?: boolean },
  ) => {
    if (options?.setLoading !== false) {
      setInitialLoading(true);
    }
    setError(null);

    try {
      console.log(`[v0] Loading session from ID: ${id}`);
      const response = await axios.get(
        `${getBackendUrl()}/supplier_enablement/check_merchant_auth/${id}`,
      );
      const sessionData = response.data;
      console.log("[v0] Session data loaded:", sessionData);

      if (sessionData?.liveViewUrl) {
        setSessionDetails(sessionData);

        if (sessionData?.activationStatus === "pending_card") {
          setShowCardInputForm(true);
        }
      } else {
        throw new Error("Invalid session data received");
      }

      return sessionData;
    } catch (err) {
      console.log("[v0] Error loading session:", err);
      if (handleApiError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load session");
    } finally {
      if (options?.setLoading !== false) {
        setInitialLoading(false);
      }
    }
  };

  // On card submission, re-fetch the session details to check updated status.
  const handleCardSubmissionSuccess = async () => {
    try {
      console.log(
        `[v0] Card submission successful, checking merchant auth for session: ${sessionId}`,
      );

      amplitude.track("Card Activation Submission Success", {
        orgId,
        sessionId,
        cardActivationId,
      });

      const sessionData = await loadSessionFromId(sessionId, {
        setLoading: false,
      });
      console.log(
        "[v0] Merchant auth check after card submission:",
        sessionData,
      );

      if (sessionData?.activationStatus === "pending_auth") {
        console.log(
          "[v0] Activation status is pending_auth, hiding card input form",
        );
        amplitude.track("Card Activation Status Updated Successfully", {
          orgId,
          sessionId,
          cardActivationId,
          activationStatus: sessionData.activationStatus,
        });
        setCardSubmissionError(null);
        setShowCardInputForm(false);
      } else {
        console.error(
          "[v0] Activation status not updated to pending_auth:",
          sessionData?.activationStatus,
        );
        amplitude.track("Card Activation Status Not Updated", {
          orgId,
          sessionId,
          cardActivationId,
          activationStatus: sessionData?.activationStatus,
        });
        setCardSubmissionError(
          "Your card details were submitted successfully, but our system is still updating. Please try refreshing the page. If the issue persists, contact support.",
        );
      }
    } catch (err) {
      console.error(
        "[v0] Error checking merchant auth after card submission:",
        err,
      );
      amplitude.track("Card Activation Verification Error", {
        orgId,
        sessionId,
        cardActivationId,
        error: err instanceof Error ? err.message : "Unknown error",
      });
      setCardSubmissionError(
        "An unexpected error occurred. Please try refreshing the page. If the issue persists, contact support.",
      );
    }
  };

  if (error) {
    return (
      <ErrorScreen
        title="Error Loading Session"
        message={error || "An error occurred"}
        onRetry={() => window.location.reload()}
        isRetrying={initialLoading}
      />
    );
  }

  // Show confirmation screen first
  if (showConfirmation) {
    return (
      <ConfirmationScreen
        vendorName={vendorName}
        vendorLogo={vendorLogo}
        organizationName={organizationName}
        title={`Log in to ${vendorName}`}
        description={
          <>
            Once you do,{" "}
            <span className="font-semibold">{organizationName}</span> will
            securely update your default payment method for{" "}
            <b>{vendorName}</b> on your behalf.
          </>
        }
        buttonText="Login"
        onContinue={() => {
          amplitude.track("Merchant Auth Confirmation Continue Clicked", {
            orgId,
            sessionId,
            cardActivationId,
          });
          setShowConfirmation(false);
        }}
      />
    );
  }

  if (showCardInputForm) {
    if (!cardActivationId || !latestSessionId) {
      return (
        <ErrorScreen
          icon="⚠️"
          title="Card Activation Not Available"
          message="The card activation information is missing. Please try reloading the page or contact support if the issue persists."
          buttonText="Reload"
          onRetry={() => window.location.reload()}
          isRetrying={initialLoading}
        />
      );
    }

    return (
      <CreditCardInputForm
        cardActivationId={cardActivationId}
        sessionId={latestSessionId}
        onSuccess={handleCardSubmissionSuccess}
        externalError={cardSubmissionError}
        onClearExternalError={() => setCardSubmissionError(null)}
      />
    );
  }

  const loadingSession = () => {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          zIndex: 10,
        }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "16px" }}>⏳</div>
          <p style={{ fontSize: "1.125rem", color: "#6b7280" }}>
            Loading login page...
          </p>
        </div>
      </div>
    );
  };

  if (sessionUrl) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fa",
          overflowY: "scroll",
        }}>
        {!iframeLoaded && loadingSession()}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "85%",
            maxWidth: "1200px",
          }}>
          {
            <Alert
              className="mt-4"
              message={`Please login to ${vendorName} using the login page below. Once you have logged in, please click the button below and your default payment method will be updated.`}
              type="info"
              showIcon></Alert>
          }
          <div
            style={{
              visibility: iframeLoaded ? "visible" : "hidden",
              width: "100%",
              height: "72vh",
              overflow: "hidden",
              position: "relative",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}>
            <iframe
              src={withNoNavbar(sessionUrl)}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                objectFit: "fill",
              }}
              title="Browserbase Live View"
              allow="clipboard-read; clipboard-write"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => {
                console.log("[v0] Iframe loaded");
                setIframeLoaded(true);
              }}
              onError={() => console.log("[v0] Iframe error")}
            />
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                color: "white",
                padding: "12px 16px",
                fontSize: "14px",
                textAlign: "center",
                zIndex: 20,
              }}>
              You're controlling the browser. Rutter will not see or store any
              of your personal login information
            </div>
          </div>
          {
            <div
              style={{
                textAlign: "center",
                width: "100%",
              }}>
              <Button
                type="primary"
                size="large"
                onClick={handleManualComplete}
                loading={isManualCompleting}
                disabled={isManualCompleting}
                style={{
                  backgroundColor: "#22c55e",
                  borderColor: "#22c55e",
                }}>
                Finish Login and Activate Card
              </Button>
              {manualCompleteError && (
                <div
                  style={{
                    marginTop: "12px",
                    color: "#dc2626",
                    fontSize: "16px",
                  }}>
                  {manualCompleteError}
                </div>
              )}
            </div>
          }
        </div>
      </div>
    );
  }
  return null;
}
